import { Config } from '@/config';
import apiService, { postOrThrow } from '@/services/api.service';
import axios, { CancelTokenSource } from 'axios';
import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { Answer, Form, MapMarker } from '@/store/form.Interface';
import { Section } from '@/store/section.interface';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, SavingOptions } from '@/store/file-uploader.interface';

const CancelToken = axios.CancelToken;
let cancelTokenForms: CancelTokenSource | null = null;

class FormsService {
  async getForms(section: Section): Promise<Form[]> {
    if (cancelTokenForms) {
      cancelTokenForms.cancel('Multiple requests.');
    }

    cancelTokenForms = CancelToken.source();

    const response = await apiService.get(
      `${Config.api.backend.urlV2}/sections/${section.id}/forms?include=statistiques,comments`,
    );

    const formsData: any = response.data;
    if (formsData) {
      const communityStore = useCommunityStore();
      communityStore.setSectionForms(formsData, section);
      communityStore.formsLoaded = true;
    }
    return response.data;
  }

  async postCommentAction(formId: number, commentId: number, action: string) {
    const response = await postOrThrow(`${Config.api.backend.urlV2}/forms/${formId}/comments/${commentId}/:${action}`);
    useCommunityStore().updateCommentAction(response.data);
    return response;
  }

  async postFormVote(answer: Answer, ficheId: number) {
    const requestData = {
      userId: useUserStore().getUserId,
      answers: answer,
    };
    const response = await postOrThrow(`${Config.api.backend.urlV2}/forms/${ficheId}/votes`, requestData);
    useCommunityStore().addVoteToFormById(response.data);
    return response;
  }

  async postComment(comment: Comment, ficheId: number) {
    const requestData = {
      userId: useUserStore().getUserId,
      comment,
    };
    const response = await postOrThrow(`${Config.api.backend.urlV2}/forms/${ficheId}/comments`, requestData);
    // A flagged comment is held for moderation: the call succeeded, but there is nothing to show on the form yet.
    if (!response.data.flagged) {
      useCommunityStore().addCommentToFormById(response.data);
    }
    return response;
  }

  async postForm(question, section_id) {
    const requestData = {
      type: 1,
      section_id: section_id,
      question: question,
      is_mandatory: 0,
      answers: {
        steps: [
          {
            id: 1,
            type: 'choices',
            answerId: 1,
            choice_template_id: 2,
          },
        ],
      },
    };
    return postOrThrow(`${Config.api.backend.urlV2}/forms/`, requestData);
  }

  async postUserForm(options: SavingOptions) {
    await FileUploaderFactory.create(options.version).upload({
      data: options.data,
      image: options.image,
      // The uploader passes a response type before the headers, which postOrThrow does not take.
      method: (url, body, _type, headers) => postOrThrow(url, body, headers),
      processorType: ProcessorType.Formidable,
      url: `${Config.api.backend.urlV2}/forms/user-forms`,
    });
  }
  async postMapVote(marker: any, form: Form) {
    const userStore = useUserStore();
    const userId = userStore.getUserId;

    const requestData = {
      userId: userId,
      answers: [
        {
          answerId: 1,
          valueId: [marker],
        },
      ],
      formType: 5,
    };
    // Rejects with an ApiError on any failure, whose status and code tell the map's cap (429, `marker-limit`) from an
    // outage.
    return postOrThrow(`${Config.api.backend.urlV2}/forms/${form.id}/votes`, requestData);
  }
}

export const formsService = new FormsService();
